import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  motion, AnimatePresence, useInView, useReducedMotion, useScroll,
  useTransform, useMotionValue, useSpring, useMotionTemplate,
} from 'framer-motion';
import {
  Phone, MapPin, Clock, Star, Mail, Dumbbell, Flame, Zap, Activity,
  Trophy, Target, TrendingUp, Users, User, Award, Timer, Repeat,
  ArrowRight, ArrowLeft, ChevronLeft, ChevronRight, ChevronDown, Check, Menu, X,
  Sparkles, MoveHorizontal, BadgeCheck, ShieldCheck, Calendar,
  Megaphone, Mountain, Heart,
} from 'lucide-react';

const SERVICE_ICONS = {
  'Group Strength': Dumbbell,
  'Conditioning': Flame,
  '1-on-1 Coaching': Target,
  'Open Gym': Activity,
};

const PROCESS = [
  {
    title: 'Book your trial',
    body: '7 days, unlimited classes, no credit card. Reserve at the desk or online — takes about 30 seconds.',
    Icon: Calendar,
    accent: '#EF4444',
  },
  {
    title: 'Intro session',
    body: 'Free 1-on-1 with a coach. We learn your goals, screen your movement, teach the squat / press / deadlift basics.',
    Icon: User,
    accent: '#F97316',
  },
  {
    title: 'Your first class',
    body: 'You walk in, we walk you in. Coach scales every movement to where you are today. You leave smoked, smiling, signed up.',
    Icon: Flame,
    accent: '#FB923C',
  },
  {
    title: 'Get strong',
    body: 'Show up 3x a week, eat real food, sleep. Six months in you will not recognize your old PRs. We will be there for every one.',
    Icon: TrendingUp,
    accent: '#FACC15',
  },
];

const SCHEDULE = [
  {
    key: 'mon',
    label: 'Mon',
    fullLabel: 'Monday',
    Icon: Dumbbell,
    accent: '#EF4444',
    accent2: '#F97316',
    focus: 'Lower body strength',
    note: 'Back squat day. Heavy triples up to a top set, then tempo work.',
    classes: [
      { time: '5:30 AM', name: 'Strength', coach: 'Coach Vee', spots: '4 left' },
      { time: '6:30 AM', name: 'Strength', coach: 'Coach Vee', spots: 'Open' },
      { time: '12:00 PM', name: 'Strength', coach: 'Coach Marco', spots: 'Open' },
      { time: '5:30 PM', name: 'Strength', coach: 'Coach Marco', spots: '2 left' },
      { time: '6:30 PM', name: 'Strength', coach: 'Coach Jen', spots: 'Open' },
    ],
  },
  {
    key: 'tue',
    label: 'Tue',
    fullLabel: 'Tuesday',
    Icon: Flame,
    accent: '#F97316',
    accent2: '#FACC15',
    focus: 'Conditioning + accessory',
    note: '30-min metcon + an upper-body push superset to finish. Bring water.',
    classes: [
      { time: '5:30 AM', name: 'Conditioning', coach: 'Coach Jen', spots: 'Open' },
      { time: '6:30 AM', name: 'Conditioning', coach: 'Coach Jen', spots: '6 left' },
      { time: '12:00 PM', name: 'Conditioning', coach: 'Coach Marco', spots: 'Open' },
      { time: '5:30 PM', name: 'Conditioning', coach: 'Coach Vee', spots: '1 left' },
      { time: '6:30 PM', name: 'Conditioning', coach: 'Coach Vee', spots: 'Open' },
    ],
  },
  {
    key: 'wed',
    label: 'Wed',
    fullLabel: 'Wednesday',
    Icon: Activity,
    accent: '#FACC15',
    accent2: '#EF4444',
    focus: 'Upper body strength',
    note: 'Press day. Heavy fives on the strict press, supersets with rows.',
    classes: [
      { time: '5:30 AM', name: 'Strength', coach: 'Coach Vee', spots: 'Open' },
      { time: '6:30 AM', name: 'Strength', coach: 'Coach Marco', spots: '3 left' },
      { time: '12:00 PM', name: 'Strength', coach: 'Coach Jen', spots: 'Open' },
      { time: '5:30 PM', name: 'Strength', coach: 'Coach Marco', spots: 'Open' },
      { time: '6:30 PM', name: 'Strength', coach: 'Coach Vee', spots: 'Open' },
    ],
  },
  {
    key: 'thu',
    label: 'Thu',
    fullLabel: 'Thursday',
    Icon: Zap,
    accent: '#EF4444',
    accent2: '#F97316',
    focus: 'Engine + carries',
    note: 'Rower intervals + heavy farmer carries. Capacity, grip, posture.',
    classes: [
      { time: '5:30 AM', name: 'Engine', coach: 'Coach Jen', spots: 'Open' },
      { time: '6:30 AM', name: 'Engine', coach: 'Coach Jen', spots: 'Open' },
      { time: '12:00 PM', name: 'Engine', coach: 'Coach Marco', spots: '5 left' },
      { time: '5:30 PM', name: 'Engine', coach: 'Coach Vee', spots: 'Open' },
      { time: '6:30 PM', name: 'Engine', coach: 'Coach Vee', spots: '2 left' },
    ],
  },
  {
    key: 'fri',
    label: 'Fri',
    fullLabel: 'Friday',
    Icon: Trophy,
    accent: '#F97316',
    accent2: '#FACC15',
    focus: 'Deadlift + finisher',
    note: 'Heavy pulls, then a 12-minute team finisher. Friday is loud.',
    classes: [
      { time: '5:30 AM', name: 'Strength', coach: 'Coach Vee', spots: 'Open' },
      { time: '6:30 AM', name: 'Strength', coach: 'Coach Vee', spots: '2 left' },
      { time: '12:00 PM', name: 'Strength', coach: 'Coach Marco', spots: 'Open' },
      { time: '5:30 PM', name: 'Strength', coach: 'Coach Jen', spots: 'Open' },
      { time: '6:30 PM', name: 'Strength', coach: 'Coach Marco', spots: 'Open' },
    ],
  },
  {
    key: 'sat',
    label: 'Sat',
    fullLabel: 'Saturday',
    Icon: Users,
    accent: '#EF4444',
    accent2: '#FACC15',
    focus: 'Partner WODs',
    note: 'Two-person workouts and the long Saturday grinder. Stay for coffee.',
    classes: [
      { time: '8:00 AM', name: 'Partner WOD', coach: 'Coach Marco', spots: '8 left' },
      { time: '9:00 AM', name: 'Partner WOD', coach: 'Coach Marco', spots: 'Open' },
      { time: '10:00 AM', name: 'Long Grinder', coach: 'Coach Vee', spots: '3 left' },
    ],
  },
  {
    key: 'sun',
    label: 'Sun',
    fullLabel: 'Sunday',
    Icon: Heart,
    accent: '#FACC15',
    accent2: '#F97316',
    focus: 'Open gym + mobility',
    note: 'Member access only — no coached classes. Roll, stretch, finish your week.',
    classes: [
      { time: 'All day', name: 'Open gym', coach: 'Self-led', spots: '24/7 access' },
    ],
  },
];

const VALUES = [
  'Strength first', 'Coach-led', 'No mirrors', 'No judgment', 'Calibrated plates',
  '24/7 member access', 'Form over ego', '600+ members', '4.9 on Google',
  'Built in Austin', 'Real coaches, real names', 'Every PR celebrated',
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

// Scoped CSS for sweat drips, plate spin, marquee, sheen, and rep flash. Class
// prefix `uf-` keeps it isolated from other custom demos' keyframes.
const UF_STYLE = `
  @keyframes uf-sheen { 0% { transform: translateX(-120%); } 60%, 100% { transform: translateX(220%); } }
  .uf-cta-sheen { position: relative; overflow: hidden; isolation: isolate; }
  .uf-cta-sheen::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.35) 50%, transparent 70%);
    transform: translateX(-120%);
    animation: uf-sheen 5.5s ease-in-out infinite;
    animation-delay: 1.4s;
    pointer-events: none;
  }
  @keyframes uf-drip {
    0%   { transform: translate3d(0,-2vh,0); opacity: 0; }
    8%   { opacity: var(--uf-op, 0.55); }
    92%  { opacity: var(--uf-op, 0.55); }
    100% { transform: translate3d(var(--uf-sway, 6px), 110vh, 0); opacity: 0; }
  }
  .uf-drip { animation: uf-drip var(--uf-dur, 7s) linear infinite; }
  @keyframes uf-plate-spin {
    0%   { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  .uf-spin { animation: uf-plate-spin var(--uf-spin-dur, 22s) linear infinite; }
  @keyframes uf-pulse-ring {
    0%   { transform: scale(0.85); opacity: 0.65; }
    70%  { transform: scale(1.4);  opacity: 0; }
    100% { transform: scale(1.4);  opacity: 0; }
  }
  .uf-pulse-ring { animation: uf-pulse-ring 2.4s ease-out infinite; }
  @keyframes uf-marquee {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
  .uf-marquee { animation: uf-marquee 36s linear infinite; }
  @keyframes uf-flash {
    0%, 100% { opacity: 0.85; }
    50%      { opacity: 1; }
  }
  .uf-flash { animation: uf-flash 1.6s ease-in-out infinite; }
  @keyframes uf-bar-rise {
    0% { transform: translateY(8px) scaleY(0.6); opacity: 0; }
    100% { transform: translateY(0) scaleY(1); opacity: 1; }
  }
  .uf-bar-rise { animation: uf-bar-rise 0.7s cubic-bezier(0.22,1,0.36,1) both; transform-origin: bottom center; }
  @keyframes uf-beam {
    0%, 100% { opacity: 0.18; }
    50%      { opacity: 0.32; }
  }
  .uf-beam { animation: uf-beam 8s ease-in-out infinite; }
  @media (prefers-reduced-motion: reduce) {
    .uf-cta-sheen::after, .uf-drip, .uf-spin, .uf-pulse-ring,
    .uf-marquee, .uf-flash, .uf-bar-rise, .uf-beam { animation: none !important; }
  }
`;

function useCountUp(target, { active, duration = 1500 } = {}) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    if (target === 0) { setValue(0); return undefined; }
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(target * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, target, duration]);
  return value;
}

function parseStat(value) {
  const m = String(value).replace(/,/g, '').match(/^(-?\d+(?:\.\d+)?)(.*)$/);
  if (!m) return null;
  return { num: parseFloat(m[1]), suffix: m[2] || '' };
}

function useTilt(intensity = 10) {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const rxs = useSpring(rx, { stiffness: 220, damping: 22 });
  const rys = useSpring(ry, { stiffness: 220, damping: 22 });
  const transform = useMotionTemplate`perspective(900px) rotateX(${rxs}deg) rotateY(${rys}deg)`;
  function onMove(e) {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry.set(px * intensity);
    rx.set(-py * intensity);
  }
  function onLeave() { rx.set(0); ry.set(0); }
  return { ref, transform, onMove, onLeave };
}

function UrbanFitnessDemo({ demo, sample = false }) {
  const [navOpen, setNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [tIndex, setTIndex] = useState(0);
  const [openFaq, setOpenFaq] = useState(0);
  const [day, setDay] = useState('mon');
  const reduceMotion = useReducedMotion();

  const phoneRaw = demo.contact.phone.replace(/[^\d+]/g, '');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!demo.testimonials?.length) return undefined;
    const id = setInterval(() => {
      setTIndex((i) => (i + 1) % demo.testimonials.length);
    }, 7500);
    return () => clearInterval(id);
  }, [demo.testimonials]);

  return (
    <div className="bg-[#0A0A0A] text-[#FAFAFA]" style={{ fontFamily: '"Inter", system-ui, sans-serif' }}>
      <style>{UF_STYLE}</style>
      <SudcoBanner sample={sample} demoName={demo.name} demoIndustry={demo.industry} />
      <TopBar phone={demo.contact.phone} phoneRaw={phoneRaw} address={demo.contact.address} />
      <Header
        demoName={demo.name}
        phone={demo.contact.phone}
        phoneRaw={phoneRaw}
        scrolled={scrolled}
        navOpen={navOpen}
        setNavOpen={setNavOpen}
        fontDisplay={demo.fontDisplay}
      />
      <Hero demo={demo} fontDisplay={demo.fontDisplay} reduceMotion={reduceMotion} />
      <ValueMarquee />
      <Stats stats={demo.stats} fontDisplay={demo.fontDisplay} />
      <Services services={demo.services} fontDisplay={demo.fontDisplay} />
      <ScheduleToggle day={day} setDay={setDay} fontDisplay={demo.fontDisplay} images={demo.gallery} />
      <Process fontDisplay={demo.fontDisplay} reduceMotion={reduceMotion} />
      <BeforeAfter images={demo.gallery} fontDisplay={demo.fontDisplay} />
      <About about={demo.about} cover={demo.cover} fontDisplay={demo.fontDisplay} />
      <Pricing featured={demo.featured} fontDisplay={demo.fontDisplay} />
      <Gallery images={demo.gallery} fontDisplay={demo.fontDisplay} />
      <Testimonials testimonials={demo.testimonials} index={tIndex} setIndex={setTIndex} fontDisplay={demo.fontDisplay} />
      <FaqSection faq={demo.faq} openFaq={openFaq} setOpenFaq={setOpenFaq} fontDisplay={demo.fontDisplay} />
      <CtaBanner phone={demo.contact.phone} phoneRaw={phoneRaw} fontDisplay={demo.fontDisplay} />
      <Contact contact={demo.contact} hours={demo.hours} fontDisplay={demo.fontDisplay} />
      <Footer demo={demo} fontDisplay={demo.fontDisplay} />
    </div>
  );
}

function SudcoBanner({ sample, demoName, demoIndustry }) {
  return (
    <div className="fixed bottom-5 right-5 z-50">
      <div className="bg-[#0B0F19]/90 border border-[#2A3142] backdrop-blur-md rounded-full px-5 py-3 shadow-[0_0_60px_-15px_rgba(99,102,241,0.5)] flex items-center gap-4 text-sm">
        <span className="text-[#8B92A8] hidden sm:inline">
          {sample
            ? <>Like this? Get one for your <span className="text-[#F5F6FA] font-medium">{demoIndustry || 'business'}</span> — <span className="text-[#6366F1] font-semibold">$800</span></>
            : <>Free demo for <span className="text-[#F5F6FA] font-medium">{demoName}</span> by <span className="text-[#6366F1] font-semibold">Sudco Solutions</span></>
          }
        </span>
        <Link to="/" className="flex items-center gap-1.5 text-[#F5F6FA] font-semibold hover:text-[#06B6D4] transition-colors whitespace-nowrap">
          <ArrowLeft size={14} /> Back to Sudco
        </Link>
      </div>
    </div>
  );
}

function TopBar({ phone, phoneRaw, address }) {
  return (
    <div className="hidden md:block bg-black text-zinc-300 text-xs border-b border-zinc-900">
      <div className="max-w-7xl mx-auto px-6 h-9 flex items-center justify-between">
        <div className="flex items-center gap-5">
          <span className="inline-flex items-center gap-1.5">
            <MapPin size={12} className="text-red-500" />{address}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Mountain size={12} className="text-red-500" />East 6th — Austin, TX
          </span>
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck size={12} className="text-red-500" />Certified coaches · Insured
          </span>
        </div>
        <div className="flex items-center gap-5">
          <span className="inline-flex items-center gap-1.5 uppercase tracking-wider font-semibold text-orange-300">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-orange-500 uf-pulse-ring" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-orange-500" />
            </span>
            Member access 24/7
          </span>
          <a href={`tel:${phoneRaw}`} className="inline-flex items-center gap-1.5 text-white font-semibold hover:text-red-400">
            <Phone size={12} className="text-red-500" />{phone}
          </a>
        </div>
      </div>
    </div>
  );
}

function PlateMark({ size = 22, className = '' }) {
  return (
    <svg viewBox="0 0 32 32" width={size} height={size} className={className} aria-hidden="true">
      <circle cx="16" cy="16" r="14" fill="currentColor" />
      <circle cx="16" cy="16" r="9" fill="rgba(255,255,255,0.08)" />
      <circle cx="16" cy="16" r="4" fill="rgba(255,255,255,0.18)" />
      <rect x="14.5" y="2" width="3" height="6" fill="rgba(255,255,255,0.15)" rx="1" />
      <rect x="14.5" y="24" width="3" height="6" fill="rgba(255,255,255,0.15)" rx="1" />
      <rect x="2" y="14.5" width="6" height="3" fill="rgba(255,255,255,0.15)" rx="1" />
      <rect x="24" y="14.5" width="6" height="3" fill="rgba(255,255,255,0.15)" rx="1" />
    </svg>
  );
}

function Header({ demoName, phone, phoneRaw, scrolled, navOpen, setNavOpen, fontDisplay }) {
  const links = [
    { href: '#classes', label: 'Classes' },
    { href: '#schedule', label: 'Schedule' },
    { href: '#start', label: 'Get started' },
    { href: '#memberships', label: 'Memberships' },
    { href: '#gallery', label: 'Gallery' },
    { href: '#faq', label: 'FAQ' },
  ];

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled
          ? 'bg-black/95 backdrop-blur shadow-[0_1px_0_0_rgba(239,68,68,0.25),0_8px_24px_-12px_rgba(0,0,0,0.7)]'
          : 'bg-[#0A0A0A] border-b border-zinc-900'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 md:h-20 flex items-center justify-between gap-4">
        <a href="#top" className="flex items-center gap-2.5 group">
          <span className="w-10 h-10 md:w-11 md:h-11 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center text-zinc-900 shadow-md group-hover:rotate-[20deg] transition-transform">
            <PlateMark size={22} className="text-zinc-900" />
          </span>
          <span
            className="font-extrabold text-xl md:text-3xl tracking-[0.04em] uppercase text-white"
            style={{ fontFamily: fontDisplay }}
          >
            {demoName}
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-7 text-sm font-bold uppercase tracking-wider text-zinc-300">
          {links.map((l) => (
            <a key={l.href} href={l.href} className="relative group">
              <span className="group-hover:text-white transition-colors">{l.label}</span>
              <span className="absolute left-0 -bottom-1 h-0.5 w-full origin-left scale-x-0 bg-red-500 transition-transform duration-300 group-hover:scale-x-100" />
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${phoneRaw}`}
            className="inline-flex items-center gap-2 text-white font-bold text-base hover:text-red-400"
          >
            <Phone size={16} />{phone}
          </a>
          <a
            href="#start"
            className="uf-cta-sheen inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm font-extrabold uppercase tracking-wider shadow-sm hover:shadow-md transition-all"
          >
            Free trial <ArrowRight size={14} />
          </a>
        </div>

        <button
          type="button"
          onClick={() => setNavOpen((v) => !v)}
          aria-label={navOpen ? 'Close menu' : 'Open menu'}
          className="lg:hidden p-2 -mr-2 text-white"
        >
          {navOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {navOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden border-t border-zinc-800 bg-black"
          >
            <div className="px-4 py-4 flex flex-col gap-1">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setNavOpen(false)}
                  className="px-3 py-2.5 rounded-md font-bold uppercase tracking-wider text-zinc-200 hover:bg-zinc-900 hover:text-white"
                >
                  {l.label}
                </a>
              ))}
              <div className="mt-2 grid grid-cols-2 gap-2">
                <a
                  href={`tel:${phoneRaw}`}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md border border-red-500/60 text-red-400 text-sm font-extrabold uppercase tracking-wider"
                >
                  <Phone size={14} />Call
                </a>
                <a
                  href="#start"
                  onClick={() => setNavOpen(false)}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md bg-red-600 text-white text-sm font-extrabold uppercase tracking-wider"
                >
                  Free trial
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function SweatField({ count = 16, scope = 'page' }) {
  const items = useMemo(
    () =>
      Array.from({ length: count }).map((_, i) => ({
        left: `${(i * 53) % 100}%`,
        size: 4 + ((i * 5) % 6),
        delay: -((i * 1.4) % 12),
        dur: 6 + ((i * 3) % 8),
        op: 0.35 + ((i * 11) % 4) * 0.1,
        sway: ((i % 2 === 0 ? 1 : -1) * (4 + ((i * 7) % 12))) + 'px',
        hue: i % 4 === 0 ? '#FB923C' : '#7DD3FC',
      })),
    [count],
  );
  return (
    <div
      className={`pointer-events-none ${scope === 'page' ? 'fixed' : 'absolute'} inset-0 overflow-hidden`}
      aria-hidden="true"
    >
      {items.map((p, i) => (
        <span
          key={i}
          className="uf-drip absolute rounded-full"
          style={{
            left: p.left,
            top: '-2vh',
            width: p.size,
            height: p.size * 1.6,
            background: `radial-gradient(circle at 50% 30%, ${p.hue}, transparent 70%)`,
            '--uf-dur': `${p.dur}s`,
            '--uf-op': p.op,
            '--uf-sway': p.sway,
            animationDelay: `${p.delay}s`,
          }}
        />
      ))}
    </div>
  );
}

function HeroBackdrop({ cover }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const imageY = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);
  const beamY = useTransform(scrollYProgress, [0, 1], ['0%', '-30%']);

  return (
    <div ref={ref} className="absolute inset-0 overflow-hidden" aria-hidden="true">
      <motion.div className="absolute inset-0" style={{ y: imageY }}>
        <img src={cover} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/40" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0A0A0A]" />
      </motion.div>

      <motion.div className="absolute inset-0" style={{ y: beamY }}>
        <div className="absolute -top-20 left-[10%] w-[60%] h-[140%] rotate-[18deg] uf-beam"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(251,146,60,0.18), transparent)' }} />
        <div className="absolute top-0 right-[10%] w-[40%] h-[120%] -rotate-[10deg] uf-beam"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(239,68,68,0.18), transparent)', animationDelay: '2s' }} />
      </motion.div>

      <div
        className="absolute inset-0 mix-blend-overlay opacity-25"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")",
        }}
      />
    </div>
  );
}

function Hero({ demo, fontDisplay, reduceMotion }) {
  return (
    <section id="top" className="relative overflow-hidden min-h-[88vh] flex items-end bg-black">
      <HeroBackdrop cover={demo.cover} />
      {!reduceMotion && <SweatField count={14} scope="section" />}

      <div className="relative w-full max-w-7xl mx-auto px-4 md:px-6 pt-20 pb-20 md:pt-28 md:pb-28">
        <motion.div
          initial="hidden"
          animate="show"
          variants={{ show: { transition: { staggerChildren: 0.1 } } }}
          className="max-w-3xl"
        >
          <motion.div
            variants={fadeUp}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-600/20 backdrop-blur border border-red-500/40 text-red-300 text-xs font-extrabold uppercase tracking-[0.2em] mb-5"
          >
            <Flame size={14} className="text-orange-400" />
            7-day trial · no credit card
          </motion.div>

          <motion.h1
            variants={fadeUp}
            className="font-extrabold text-6xl sm:text-7xl md:text-8xl leading-[0.92] tracking-tight uppercase text-white mb-6"
            style={{ fontFamily: fontDisplay }}
          >
            Get strong.
            <span className="block text-red-500">
              Stay strong.
              <svg
                viewBox="0 0 240 14"
                className="absolute hidden md:block w-60 h-3 mt-1"
                aria-hidden="true"
              >
                <path
                  d="M2,11 C40,-1 100,14 238,4"
                  stroke="#F97316"
                  strokeWidth="3"
                  fill="none"
                  strokeLinecap="round"
                  className="uf-flash"
                />
              </svg>
            </span>
            <span className="block text-zinc-200">No mirrors required.</span>
          </motion.h1>

          <motion.p variants={fadeUp} className="text-lg md:text-xl text-zinc-300 leading-relaxed max-w-2xl mb-8">
            {demo.tagline} Coach-led classes 5 AM to 7 PM, 24/7 member access for the rest.
          </motion.p>

          <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-9 text-sm text-zinc-300">
            <span className="inline-flex items-center gap-1.5">
              <span className="flex">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={14} className="fill-orange-400 text-orange-400" />
                ))}
              </span>
              <strong className="text-white">4.9</strong> · 380+ Google reviews
            </span>
            <span className="inline-flex items-center gap-1.5"><BadgeCheck size={14} className="text-red-500" /> Certified coaches</span>
            <span className="inline-flex items-center gap-1.5"><Users size={14} className="text-red-500" /> 600+ members strong</span>
          </motion.div>

          <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
            <a
              href="#start"
              className="uf-cta-sheen inline-flex items-center gap-2 px-7 py-4 rounded-md bg-red-600 hover:bg-red-700 text-white text-base font-extrabold uppercase tracking-wider shadow-xl shadow-red-900/40 transition-all hover:scale-[1.02]"
            >
              <Flame size={18} />Start your free 7 days
            </a>
            <a
              href="#schedule"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md bg-white/10 backdrop-blur hover:bg-white/15 text-white text-base font-bold uppercase tracking-wider border border-white/20"
            >
              See the schedule <ArrowRight size={16} />
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function ValueMarquee() {
  const items = [...VALUES, ...VALUES];
  return (
    <div className="bg-red-600 text-white overflow-hidden border-y border-red-700">
      <div className="uf-marquee py-3 flex items-center gap-10 whitespace-nowrap text-sm font-extrabold uppercase tracking-[0.18em]">
        {items.map((v, i) => (
          <span key={i} className="inline-flex items-center gap-3 flex-shrink-0">
            <Dumbbell size={14} className="text-orange-200" />
            <span>{v}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function Stats({ stats, fontDisplay }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  if (!stats?.length) return null;
  return (
    <section ref={ref} className="bg-[#0A0A0A] relative overflow-hidden">
      <div className="absolute inset-0 opacity-30 pointer-events-none" aria-hidden="true" style={{
        backgroundImage:
          'radial-gradient(circle at 20% 50%, rgba(239,68,68,0.18) 0%, transparent 40%), radial-gradient(circle at 80% 50%, rgba(249,115,22,0.18) 0%, transparent 40%)',
      }} />
      <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {stats.map((s, i) => (
            <StatCard key={s.label} stat={s} active={inView} fontDisplay={fontDisplay} delay={i * 0.08} />
          ))}
        </div>
      </div>
    </section>
  );
}

function StatCard({ stat, active, fontDisplay, delay }) {
  const parsed = parseStat(stat.value);
  const numeric = useCountUp(parsed?.num ?? 0, { active });
  const display = parsed
    ? `${parsed.num >= 1000 ? Math.round(numeric).toLocaleString() : numeric.toFixed(parsed.num % 1 ? 1 : 0)}${parsed.suffix}`
    : stat.value;
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={active ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
      className="relative group"
    >
      <div className="absolute -top-2 left-6 w-10 h-10 rounded-b-full bg-gradient-to-b from-red-500 to-red-700 transform-gpu transition-transform group-hover:scale-110 group-hover:-translate-y-1" />
      <Dumbbell className="absolute -top-1.5 left-7 text-red-100" size={20} />
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-6 md:p-7 text-center shadow-lg group-hover:border-red-500/40 transition-colors">
        <div
          className="font-extrabold text-4xl md:text-5xl text-white leading-none tabular-nums tracking-wide"
          style={{ fontFamily: fontDisplay }}
        >
          {display}
        </div>
        <div className="mt-3 text-xs md:text-sm font-extrabold text-zinc-400 uppercase tracking-[0.18em]">
          {stat.label}
        </div>
      </div>
    </motion.div>
  );
}

function ServiceCard({ service, index, fontDisplay }) {
  const Icon = SERVICE_ICONS[service.title] || Dumbbell;
  const tilt = useTilt(8);
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.55, delay: index * 0.08 }}
      ref={tilt.ref}
      onMouseMove={tilt.onMove}
      onMouseLeave={tilt.onLeave}
      style={{ transform: tilt.transform, transformStyle: 'preserve-3d' }}
      className="relative group"
    >
      <div className="absolute -inset-1 rounded-3xl bg-gradient-to-br from-red-500/0 via-red-500/40 to-orange-500/40 opacity-0 group-hover:opacity-100 blur-xl transition-opacity" aria-hidden="true" />
      <a
        href="#start"
        className="relative block rounded-2xl bg-zinc-900 border border-zinc-800 p-7 hover:border-red-500/60 transition-colors overflow-hidden h-full"
        style={{ transform: 'translateZ(0)' }}
      >
        <div className="absolute -right-4 -top-4 w-32 h-32 rounded-full bg-red-500/5 group-hover:bg-red-500/10 transition-colors" aria-hidden="true" />
        <div className="absolute -right-6 -top-6 opacity-10 text-red-500 group-hover:opacity-25 group-hover:rotate-12 transition-all duration-500">
          <Icon size={120} />
        </div>
        <div className="relative">
          <span className="inline-flex w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 text-white items-center justify-center mb-5 shadow-md group-hover:scale-110 group-hover:rotate-6 transition-transform">
            <Icon size={22} />
          </span>
          <h3
            className="font-extrabold text-2xl text-white mb-2 group-hover:text-red-400 transition-colors uppercase tracking-wide"
            style={{ fontFamily: fontDisplay }}
          >
            {service.title}
          </h3>
          <p className="text-zinc-400 leading-relaxed text-[15px] mb-5">{service.desc}</p>
          <span className="inline-flex items-center gap-1 text-sm font-extrabold uppercase tracking-wider text-red-400">
            Try a class
            <ArrowRight
              size={14}
              className="transition-transform group-hover:translate-x-1"
            />
          </span>
        </div>
      </a>
    </motion.div>
  );
}

function Services({ services, fontDisplay }) {
  return (
    <section id="classes" className="relative py-20 md:py-28 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="max-w-2xl mb-14">
          <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-orange-400 mb-2">What we run</p>
          <h2
            className="font-extrabold text-5xl md:text-6xl text-white leading-[0.95] uppercase tracking-tight"
            style={{ fontFamily: fontDisplay }}
          >
            Strength, sweat,
            <span className="block text-red-500">community.</span>
          </h2>
          <p className="mt-4 text-zinc-400 leading-relaxed text-lg">
            Four programs, one room. Pick a track or rotate — the coach scales every movement to where you actually are.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s, i) => (
            <ServiceCard key={s.title} service={s} index={i} fontDisplay={fontDisplay} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ScheduleToggle({ day, setDay, fontDisplay, images }) {
  const cur = SCHEDULE.find((s) => s.key === day) || SCHEDULE[0];
  const Cur = cur.Icon;
  const heroImage = images[SCHEDULE.findIndex((s) => s.key === day) % images.length];
  return (
    <section
      id="schedule"
      className="relative py-20 md:py-28 overflow-hidden transition-colors duration-700 bg-zinc-950"
    >
      <div
        className="absolute inset-0 opacity-30 transition-opacity duration-700 pointer-events-none"
        aria-hidden="true"
        style={{
          background: `radial-gradient(circle at 30% 40%, ${cur.accent}33 0%, transparent 45%), radial-gradient(circle at 75% 70%, ${cur.accent2}22 0%, transparent 45%)`,
        }}
      />
      <div className="relative max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-start">
          <div>
            <p
              className="text-xs font-extrabold uppercase tracking-[0.22em] mb-2 transition-colors"
              style={{ color: cur.accent }}
            >
              This week's schedule
            </p>
            <h2
              className="font-extrabold text-5xl md:text-6xl text-white leading-[0.95] uppercase mb-5"
              style={{ fontFamily: fontDisplay }}
            >
              Seven days,
              <span className="block transition-colors" style={{ color: cur.accent }}>
                85 classes
              </span>
              all coached.
            </h2>
            <p className="text-zinc-400 leading-relaxed text-lg mb-7">
              Same time slots Mon–Fri so you can plan your week. Saturday brings partner WODs, Sunday is open gym for members. Reserve your spot in the app — most classes fill 24 hours out.
            </p>

            <div className="grid grid-cols-4 sm:grid-cols-7 gap-2 mb-7">
              {SCHEDULE.map((s) => {
                const SIcon = s.Icon;
                const active = s.key === day;
                return (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setDay(s.key)}
                    className={`relative p-3 rounded-xl border transition-all overflow-hidden ${
                      active
                        ? 'border-transparent text-white shadow-lg scale-105'
                        : 'bg-zinc-900 border-zinc-800 hover:border-zinc-700 text-zinc-300'
                    }`}
                    style={active ? { background: `linear-gradient(135deg, ${s.accent}, ${s.accent2})` } : undefined}
                  >
                    <SIcon size={18} className={`mb-1 mx-auto ${active ? 'text-white' : 'text-zinc-400'}`} />
                    <div className="font-extrabold text-xs uppercase tracking-wider">{s.label}</div>
                    {active && (
                      <motion.span
                        layoutId="day-glow"
                        className="absolute inset-0 rounded-xl ring-2 ring-white/40 pointer-events-none"
                      />
                    )}
                  </button>
                );
              })}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={day}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3 }}
                className="rounded-2xl bg-zinc-900/80 border border-zinc-800 overflow-hidden shadow-xl"
              >
                <div className="px-5 py-4 flex items-center gap-3 border-b border-zinc-800"
                  style={{ background: `linear-gradient(90deg, ${cur.accent}22, transparent)` }}>
                  <span
                    className="w-11 h-11 rounded-xl flex items-center justify-center text-white shadow-md"
                    style={{ background: `linear-gradient(135deg, ${cur.accent}, ${cur.accent2})` }}
                  >
                    <Cur size={20} />
                  </span>
                  <div>
                    <div
                      className="font-extrabold text-xl text-white uppercase tracking-wide"
                      style={{ fontFamily: fontDisplay }}
                    >
                      {cur.fullLabel} · {cur.focus}
                    </div>
                    <div className="text-sm text-zinc-400 mt-0.5">{cur.note}</div>
                  </div>
                </div>
                <ul className="divide-y divide-zinc-800">
                  {cur.classes.map((c, idx) => {
                    const open = c.spots === 'Open' || c.spots.includes('24/7');
                    const tight = !open && (c.spots.startsWith('1 ') || c.spots.startsWith('2 '));
                    return (
                      <li key={idx} className="px-5 py-3 flex items-center justify-between gap-4 hover:bg-zinc-900 transition-colors">
                        <div className="flex items-center gap-4 min-w-0">
                          <span className="font-extrabold text-white tabular-nums w-20 flex-shrink-0" style={{ fontFamily: fontDisplay }}>
                            {c.time}
                          </span>
                          <div className="min-w-0">
                            <div className="font-bold text-zinc-100 truncate">{c.name}</div>
                            <div className="text-xs text-zinc-500">{c.coach}</div>
                          </div>
                        </div>
                        <span
                          className={`flex-shrink-0 text-[11px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full ${
                            tight
                              ? 'bg-orange-500/20 text-orange-300 border border-orange-500/40'
                              : open
                                ? 'bg-zinc-800 text-zinc-300 border border-zinc-700'
                                : 'bg-red-500/20 text-red-300 border border-red-500/40'
                          }`}
                        >
                          {c.spots}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={day}
                initial={{ opacity: 0, scale: 0.96, rotate: -1 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                exit={{ opacity: 0, scale: 1.04, rotate: 1 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="relative aspect-[4/5] sm:aspect-[5/6] rounded-3xl overflow-hidden shadow-2xl"
                style={{ boxShadow: `0 30px 80px -20px ${cur.accent}55` }}
              >
                <img
                  src={heroImage}
                  alt={`${cur.fullLabel} class`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div
                  className="absolute inset-0 mix-blend-multiply"
                  style={{ background: `linear-gradient(180deg, transparent 40%, ${cur.accent}66 100%)` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <div className="absolute top-5 left-5">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur border border-white/10 text-white text-[11px] font-extrabold uppercase tracking-wider">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 uf-flash" />
                    {cur.classes.length} session{cur.classes.length === 1 ? '' : 's'} today
                  </span>
                </div>
                <div className="absolute bottom-5 left-5 right-5 text-white">
                  <div className="text-xs font-extrabold uppercase tracking-[0.22em] opacity-80">{cur.fullLabel}</div>
                  <div className="text-3xl md:text-4xl font-extrabold mt-1 uppercase tracking-tight" style={{ fontFamily: fontDisplay }}>
                    {cur.focus}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute -top-4 -right-4 w-20 h-20 rounded-2xl bg-zinc-900 shadow-xl border border-zinc-800 flex items-center justify-center"
            >
              <span
                className="w-12 h-12 rounded-xl flex items-center justify-center text-white"
                style={{ background: `linear-gradient(135deg, ${cur.accent}, ${cur.accent2})` }}
              >
                <Cur size={22} />
              </span>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Process({ fontDisplay, reduceMotion }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const lineHeight = useTransform(scrollYProgress, [0.15, 0.85], ['0%', '100%']);
  return (
    <section id="start" ref={ref} className="relative py-20 md:py-28 bg-black text-white overflow-hidden">
      <div className="absolute inset-0 opacity-40 pointer-events-none" aria-hidden="true" style={{
        backgroundImage:
          'radial-gradient(circle at 80% 20%, rgba(239,68,68,0.25) 0%, transparent 40%), radial-gradient(circle at 10% 80%, rgba(249,115,22,0.2) 0%, transparent 35%)',
      }} />

      <div className="relative max-w-7xl mx-auto px-4 md:px-6">
        <div className="max-w-2xl mb-14 md:mb-20">
          <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-orange-400 mb-2">Get started</p>
          <h2
            className="font-extrabold text-5xl md:text-6xl leading-[0.95] uppercase tracking-tight"
            style={{ fontFamily: fontDisplay }}
          >
            Four steps
            <span className="block text-red-500">from couch to PR.</span>
          </h2>
          <p className="mt-4 text-zinc-400 leading-relaxed text-lg max-w-xl">
            Most members are training inside of a week. The trial is real — no card, no contract, no high-pressure tour.
          </p>
        </div>

        <div className="relative grid md:grid-cols-2 gap-10 md:gap-x-20 md:gap-y-24">
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 -translate-x-1/2 w-px bg-zinc-800">
            {!reduceMotion && (
              <motion.div
                style={{ height: lineHeight }}
                className="absolute left-0 top-0 w-full bg-gradient-to-b from-red-500 via-orange-500 to-amber-400"
              />
            )}
          </div>

          {PROCESS.map((step, i) => (
            <ProcessStep
              key={step.title}
              step={step}
              index={i}
              fontDisplay={fontDisplay}
              align={i % 2 === 0 ? 'left' : 'right'}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function ProcessStep({ step, index, fontDisplay, align }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const Icon = step.Icon;
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: align === 'left' ? -30 : 30 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`relative ${align === 'right' ? 'md:col-start-2' : ''}`}
    >
      <div className="hidden md:block absolute top-6 -translate-y-1/2 z-10"
        style={{ [align === 'left' ? 'right' : 'left']: '-2.5rem' }}
      >
        <div
          className="w-5 h-5 rounded-full ring-4 ring-black transition-all"
          style={{ background: step.accent, boxShadow: inView ? `0 0 24px ${step.accent}` : undefined }}
        />
      </div>
      <div className="rounded-2xl bg-zinc-900/70 border border-zinc-800 backdrop-blur p-6 md:p-7 hover:bg-zinc-900 hover:border-zinc-700 transition-colors">
        <div className="flex items-center gap-3 mb-4">
          <span
            className="w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-lg"
            style={{ background: `linear-gradient(135deg, ${step.accent}, ${step.accent}cc)` }}
          >
            <Icon size={22} />
          </span>
          <span
            className="font-extrabold text-5xl tabular-nums leading-none uppercase"
            style={{ fontFamily: fontDisplay, color: step.accent, opacity: 0.55 }}
          >
            {String(index + 1).padStart(2, '0')}
          </span>
        </div>
        <h3
          className="font-extrabold text-2xl md:text-3xl text-white mb-3 leading-tight uppercase tracking-wide"
          style={{ fontFamily: fontDisplay }}
        >
          {step.title}
        </h3>
        <p className="text-zinc-300/90 text-[15px] leading-relaxed">{step.body}</p>
      </div>
    </motion.div>
  );
}

function BeforeAfter({ images, fontDisplay }) {
  const [pct, setPct] = useState(50);
  const dragging = useRef(false);
  const containerRef = useRef(null);

  function setFromClientX(clientX) {
    const el = containerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const p = ((clientX - r.left) / r.width) * 100;
    setPct(Math.max(0, Math.min(100, p)));
  }

  useEffect(() => {
    function move(e) {
      if (!dragging.current) return;
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      setFromClientX(cx);
    }
    function up() { dragging.current = false; }
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', move, { passive: true });
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', up);
    };
  }, []);

  const before = images?.[3] || images?.[0];
  const after = images?.[0];

  return (
    <section className="relative py-20 md:py-28 bg-[#0A0A0A] overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="max-w-2xl mb-12">
          <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-orange-400 mb-2">A real member</p>
          <h2
            className="font-extrabold text-5xl md:text-6xl text-white leading-[0.95] uppercase tracking-tight"
            style={{ fontFamily: fontDisplay }}
          >
            Drag to see
            <span className="block text-red-500">12 months of work.</span>
          </h2>
          <p className="mt-4 text-zinc-400 leading-relaxed">
            Devin R., 38. Started in March, never lifted a barbell before. Down 40 lb, deadlift went from 135 to 410. No magic — just three sessions a week and a coach in his ear.
          </p>
        </div>

        <div
          ref={containerRef}
          className="relative aspect-[16/9] md:aspect-[21/9] rounded-3xl overflow-hidden shadow-2xl select-none cursor-ew-resize touch-none border border-zinc-800"
          onMouseDown={(e) => { dragging.current = true; setFromClientX(e.clientX); }}
          onTouchStart={(e) => { dragging.current = true; setFromClientX(e.touches[0].clientX); }}
        >
          <img src={after} alt="After: 12 months in" className="absolute inset-0 w-full h-full object-cover" />
          <span className="absolute top-5 right-5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-600 text-white text-xs font-extrabold uppercase tracking-wider shadow-lg">
            <Trophy size={12} />After
          </span>

          <div className="absolute inset-0 overflow-hidden" style={{ width: `${pct}%` }}>
            <img
              src={before}
              alt="Before"
              className="absolute inset-0 w-screen max-w-none h-full object-cover grayscale"
              style={{ width: containerRef.current ? `${containerRef.current.getBoundingClientRect().width}px` : '100%' }}
            />
            <span className="absolute top-5 left-5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-700 text-white text-xs font-extrabold uppercase tracking-wider shadow-lg">
              <Calendar size={12} />Day 1
            </span>
          </div>

          <div
            className="absolute top-0 bottom-0 w-1 bg-red-500 shadow-[0_0_24px_rgba(239,68,68,0.6)]"
            style={{ left: `calc(${pct}% - 2px)` }}
          />
          <div
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-14 h-14 rounded-full bg-white shadow-[0_8px_28px_rgba(0,0,0,0.5)] flex items-center justify-center text-zinc-900 cursor-ew-resize border-2 border-red-500"
            style={{ left: `${pct}%` }}
            aria-label="Drag to compare"
          >
            <MoveHorizontal size={24} />
          </div>

          <div className="hidden md:block absolute bottom-5 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-black/80 backdrop-blur text-white text-xs font-extrabold uppercase tracking-wider shadow border border-zinc-700">
            ← drag to compare →
          </div>
        </div>

        <div className="mt-6 grid sm:grid-cols-3 gap-4 text-sm">
          <StatRow icon={Repeat} label="Sessions" value="156" />
          <StatRow icon={TrendingUp} label="Deadlift PR" value="+275 lb" />
          <StatRow icon={Timer} label="Time invested" value="12 mo" />
        </div>
      </div>
    </section>
  );
}

function StatRow({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4 flex items-center gap-3">
      <span className="w-10 h-10 rounded-lg bg-red-500/15 text-red-400 flex items-center justify-center flex-shrink-0">
        <Icon size={18} />
      </span>
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 font-extrabold">{label}</div>
        <div className="text-white font-extrabold text-lg">{value}</div>
      </div>
    </div>
  );
}

function About({ about, cover, fontDisplay }) {
  const tilt = useTilt(6);
  return (
    <section className="relative py-20 md:py-28 bg-zinc-950 overflow-hidden">
      <div className="absolute -left-40 top-1/4 w-96 h-96 rounded-full bg-red-500/10 blur-3xl pointer-events-none" />
      <div className="absolute -right-32 bottom-0 w-80 h-80 rounded-full bg-orange-500/10 blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 md:px-6 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div>
          <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-orange-400 mb-2">Our story</p>
          <h2
            className="font-extrabold text-5xl md:text-6xl text-white leading-[0.95] uppercase tracking-tight mb-6"
            style={{ fontFamily: fontDisplay }}
          >
            {about?.heading || 'Built by lifters, for lifters.'}
          </h2>
          {about?.body?.split('\n\n').map((para, i) => (
            <p key={i} className="text-zinc-300 leading-relaxed mb-4 text-[15px] md:text-base">{para}</p>
          ))}
          {about?.signature && (
            <div className="mt-6 flex items-center gap-3">
              <span className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-orange-600 text-white flex items-center justify-center font-extrabold uppercase">
                UF
              </span>
              <div>
                <p
                  className="text-white font-bold leading-tight uppercase tracking-wide text-lg"
                  style={{ fontFamily: fontDisplay }}
                >
                  {about.signature}
                </p>
                <p className="text-xs text-orange-300/80 uppercase tracking-[0.2em] mt-0.5">Coaches & owners</p>
              </div>
            </div>
          )}
        </div>

        <motion.div
          ref={tilt.ref}
          onMouseMove={tilt.onMove}
          onMouseLeave={tilt.onLeave}
          style={{ transform: tilt.transform, transformStyle: 'preserve-3d' }}
          className="relative"
        >
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl border border-zinc-800" style={{ transform: 'translateZ(40px)' }}>
            <img src={cover} alt="Urban Fitness Collective gym" className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          </div>
          <div
            className="absolute -bottom-6 -left-6 bg-zinc-900 rounded-2xl shadow-xl border border-zinc-800 p-5 max-w-xs"
            style={{ transform: 'translateZ(70px)' }}
          >
            <div className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.2em] text-orange-400 mb-1">
              <Award size={14} />East Austin original
            </div>
            <p className="text-zinc-200 text-sm font-semibold leading-snug">
              "Calibrated plates. Sled track. No commission, no scripts. Just barbells and your name."
            </p>
          </div>
          <motion.div
            animate={{ y: [0, -10, 0], rotate: [-3, 3, -3] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute -top-5 -right-3"
            style={{ transform: 'translateZ(80px)' }}
          >
            <div className="w-24 h-24 rounded-2xl bg-red-600 shadow-xl flex items-center justify-center text-white rotate-6 border-4 border-zinc-900">
              <div className="text-center">
                <div className="text-3xl font-extrabold leading-none uppercase" style={{ fontFamily: fontDisplay }}>12</div>
                <div className="text-[9px] font-extrabold uppercase tracking-[0.2em] mt-1">Coaches</div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function Pricing({ featured, fontDisplay }) {
  if (!featured?.items?.length) return null;
  return (
    <section id="memberships" className="py-20 md:py-28 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="max-w-2xl mb-14">
          {featured.kicker && (
            <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-orange-400 mb-2">{featured.kicker}</p>
          )}
          <h2
            className="font-extrabold text-5xl md:text-6xl text-white leading-[0.95] uppercase tracking-tight"
            style={{ fontFamily: fontDisplay }}
          >
            {featured.heading || 'Pick your level.'}
          </h2>
          <p className="mt-4 text-zinc-400 leading-relaxed text-lg">
            No contracts. No startup fees. Cancel anytime by replying to your renewal email — we read those personally.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {featured.items.map((item, i) => (
            <PricingCard key={item.name} item={item} index={i} fontDisplay={fontDisplay} />
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingCard({ item, index, fontDisplay }) {
  const tilt = useTilt(4);
  const featured = item.tag && item.tag.toLowerCase().includes('save');
  return (
    <motion.div
      initial={{ opacity: 0, y: 24, scale: 0.97 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, delay: index * 0.06 }}
      ref={tilt.ref}
      onMouseMove={tilt.onMove}
      onMouseLeave={tilt.onLeave}
      style={{ transform: tilt.transform, transformStyle: 'preserve-3d' }}
      className={`relative rounded-2xl border p-6 transition-shadow hover:shadow-2xl ${
        featured
          ? 'border-red-500/60 bg-gradient-to-br from-red-500/15 via-zinc-900 to-zinc-900 shadow-lg shadow-red-900/30'
          : item.tag
            ? 'border-orange-500/40 bg-gradient-to-br from-orange-500/10 to-zinc-900'
            : 'border-zinc-800 bg-zinc-900'
      }`}
    >
      {item.tag && (
        <span
          className={`absolute -top-3 left-6 inline-flex items-center gap-1 px-3 py-1 rounded-full text-white text-[11px] font-extrabold uppercase tracking-wider shadow ${
            featured ? 'bg-red-600' : 'bg-orange-500'
          }`}
        >
          <Sparkles size={11} />{item.tag}
        </span>
      )}
      <h3
        className="font-extrabold text-2xl text-white leading-tight pr-4 uppercase tracking-wide"
        style={{ fontFamily: fontDisplay }}
      >
        {item.name}
      </h3>
      <p className="mt-2 text-zinc-400 text-sm leading-relaxed min-h-[3.5rem]">{item.desc}</p>
      <div className="mt-5 pt-4 border-t border-zinc-800 flex items-end justify-between gap-3">
        <span
          className="font-extrabold text-3xl text-red-400 leading-none tracking-wide"
          style={{ fontFamily: fontDisplay }}
        >
          {item.price}
        </span>
        <a
          href="#start"
          className="inline-flex items-center gap-1 text-xs font-extrabold uppercase tracking-wider text-zinc-300 hover:text-white"
        >
          Sign up <ArrowRight size={12} />
        </a>
      </div>
    </motion.div>
  );
}

const GALLERY_TAGS = [
  { key: 'all', label: 'All' },
  { key: 'strength', label: 'Strength' },
  { key: 'conditioning', label: 'Conditioning' },
  { key: 'community', label: 'Community' },
];

function Gallery({ images, fontDisplay }) {
  const [filter, setFilter] = useState('all');
  const [lightbox, setLightbox] = useState(null);

  const captions = [
    { caption: 'Friday deadlift session', tag: 'strength' },
    { caption: 'Saturday partner WOD', tag: 'community' },
    { caption: 'Tuesday metcon, sled finisher', tag: 'conditioning' },
    { caption: 'New PR on the squat', tag: 'strength' },
  ];
  const items = images.map((src, i) => ({ src, ...captions[i % captions.length] }));
  const filtered = filter === 'all' ? items : items.filter((it) => it.tag === filter);

  return (
    <section id="gallery" className="relative py-20 md:py-28 bg-zinc-950">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div className="max-w-2xl">
            <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-orange-400 mb-2">Inside the gym</p>
            <h2
              className="font-extrabold text-5xl md:text-6xl text-white leading-[0.95] uppercase tracking-tight"
              style={{ fontFamily: fontDisplay }}
            >
              Real members,
              <span className="block text-red-500">real reps.</span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {GALLERY_TAGS.map((g) => {
              const active = filter === g.key;
              return (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => setFilter(g.key)}
                  className={`relative px-4 py-2 rounded-full text-sm font-extrabold uppercase tracking-wider transition-colors ${
                    active
                      ? 'bg-red-600 text-white shadow-md'
                      : 'bg-zinc-900 text-zinc-300 border border-zinc-800 hover:border-zinc-700'
                  }`}
                >
                  {active && (
                    <motion.span
                      layoutId="gallery-pill"
                      className="absolute inset-0 rounded-full bg-red-600"
                      style={{ zIndex: -1 }}
                    />
                  )}
                  <span className="relative">{g.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <motion.div layout className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <AnimatePresence>
            {filtered.map((it, i) => (
              <motion.button
                key={it.src + filter}
                layout
                type="button"
                onClick={() => setLightbox(images.indexOf(it.src))}
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.92 }}
                transition={{ duration: 0.45, delay: i * 0.05 }}
                whileHover={{ y: -4 }}
                className={`group relative rounded-2xl overflow-hidden bg-zinc-800 ${
                  i === 0 ? 'col-span-2 md:row-span-2 md:col-span-2 aspect-[4/3] md:aspect-auto' : 'aspect-square'
                }`}
              >
                <img
                  src={it.src}
                  alt={it.caption}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent opacity-95 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-3 left-3 right-3 text-white">
                  <span className="inline-block px-2 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-extrabold uppercase tracking-wider mb-1">
                    {it.tag}
                  </span>
                  <p className="text-xs md:text-sm font-semibold leading-snug">{it.caption}</p>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 backdrop-blur flex items-center justify-center p-4 md:p-8"
            onClick={() => setLightbox(null)}
          >
            <motion.img
              key={images[lightbox]}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              src={images[lightbox]}
              alt=""
              className="max-w-5xl max-h-[80vh] object-contain rounded-xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              type="button"
              onClick={() => setLightbox(null)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
              aria-label="Close"
            >
              <X size={20} />
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 text-white text-sm">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + images.length) % images.length); }}
                className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"
                aria-label="Previous"
              >
                <ChevronLeft size={18} />
              </button>
              <span className="font-semibold">{lightbox + 1} / {images.length}</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % images.length); }}
                className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"
                aria-label="Next"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function Testimonials({ testimonials, index, setIndex, fontDisplay }) {
  if (!testimonials?.length) return null;
  const total = testimonials.length;
  const t = testimonials[index];
  return (
    <section className="py-20 md:py-28 bg-[#0A0A0A] relative overflow-hidden">
      <div className="absolute top-12 left-8 text-zinc-800" aria-hidden="true">
        <svg width="120" height="120" viewBox="0 0 120 120">
          <text x="0" y="100" fontSize="180" fontFamily="Bebas Neue, Impact, sans-serif" fill="currentColor">"</text>
        </svg>
      </div>
      <div className="absolute -right-20 bottom-0 opacity-10 uf-spin" style={{ '--uf-spin-dur': '30s' }} aria-hidden="true">
        <PlateMark size={320} className="text-red-500" />
      </div>

      <div className="relative max-w-5xl mx-auto px-4 md:px-6 text-center">
        <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-orange-400 mb-2">Word in the gym</p>
        <h2
          className="font-extrabold text-5xl md:text-6xl text-white leading-[0.95] uppercase tracking-tight mb-3"
          style={{ fontFamily: fontDisplay }}
        >
          From the <span className="text-red-500">collective</span>.
        </h2>
        <div className="flex items-center justify-center gap-1 mb-12">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} size={20} className="fill-orange-400 text-orange-400" />
          ))}
          <span className="ml-2 text-zinc-400 font-semibold">4.9 on Google · 380+ reviews</span>
        </div>

        <div className="relative min-h-[260px] md:min-h-[200px]">
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={index}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.5 }}
              className="absolute inset-0"
            >
              <p
                className="text-2xl md:text-3xl leading-relaxed text-white max-w-3xl mx-auto"
                style={{ fontFamily: '"Inter", sans-serif', fontWeight: 500, fontStyle: 'italic' }}
              >
                "{t.quote}"
              </p>
              <footer className="mt-7 text-sm text-zinc-400 font-extrabold uppercase tracking-[0.18em]">
                — {t.author}
              </footer>
            </motion.blockquote>
          </AnimatePresence>
        </div>

        <div className="mt-10 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => setIndex((i) => (i - 1 + total) % total)}
            aria-label="Previous review"
            className="w-11 h-11 rounded-full border border-zinc-700 hover:border-red-500 hover:text-white flex items-center justify-center transition-colors bg-zinc-900 text-zinc-300"
          >
            <ChevronLeft size={18} />
          </button>
          <div className="flex items-center gap-1.5">
            {testimonials.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Review ${i + 1}`}
                className={`h-2 rounded-full transition-all ${
                  i === index ? 'w-8 bg-red-500' : 'w-2 bg-zinc-700 hover:bg-zinc-600'
                }`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => setIndex((i) => (i + 1) % total)}
            aria-label="Next review"
            className="w-11 h-11 rounded-full border border-zinc-700 hover:border-red-500 hover:text-white flex items-center justify-center transition-colors bg-zinc-900 text-zinc-300"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
}

function FaqSection({ faq, openFaq, setOpenFaq, fontDisplay }) {
  return (
    <section id="faq" className="py-20 md:py-28 bg-zinc-950 border-y border-zinc-900">
      <div className="max-w-5xl mx-auto px-4 md:px-6 grid md:grid-cols-[1fr_1.4fr] gap-10 md:gap-16">
        <div>
          <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-orange-400 mb-2">Common questions</p>
          <h2
            className="font-extrabold text-5xl md:text-6xl text-white leading-[0.95] uppercase tracking-tight"
            style={{ fontFamily: fontDisplay }}
          >
            Asked &
            <span className="block text-red-500">answered.</span>
          </h2>
          <p className="mt-3 text-zinc-400 leading-relaxed">
            Don't see yours? <a href="#contact" className="text-red-400 font-extrabold uppercase tracking-wider underline">Send a note</a> — we reply same day.
          </p>
        </div>
        <div className="space-y-2">
          {faq.map((item, i) => {
            const open = openFaq === i;
            return (
              <div
                key={item.q}
                className={`rounded-xl border transition-all ${
                  open ? 'bg-zinc-900 border-red-500/60 shadow-lg shadow-red-900/20' : 'bg-zinc-900/60 border-zinc-800'
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(open ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                  aria-expanded={open}
                >
                  <span className="font-bold text-white text-[15px]">{item.q}</span>
                  <ChevronDown
                    size={18}
                    className={`flex-shrink-0 text-red-400 transition-transform ${open ? 'rotate-180' : ''}`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {open && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <p className="px-5 pb-5 text-zinc-300 leading-relaxed text-[15px]">{item.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CtaBanner({ phone, phoneRaw, fontDisplay }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 50, y: 50 });
  function onMove(e) {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
  }
  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      className="relative overflow-hidden bg-gradient-to-br from-red-700 via-red-600 to-orange-600 text-white"
    >
      <div
        className="absolute inset-0 transition-opacity pointer-events-none"
        style={{
          background: `radial-gradient(circle at ${pos.x}% ${pos.y}%, rgba(255,255,255,0.25), transparent 40%)`,
        }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 opacity-30" aria-hidden="true" style={{
        backgroundImage:
          'radial-gradient(circle at 20% 100%, rgba(0,0,0,0.25), transparent 40%), radial-gradient(circle at 80% 0%, rgba(255,255,255,0.18), transparent 40%)',
      }} />
      <div className="absolute -left-12 top-1/2 -translate-y-1/2 opacity-20 uf-spin" style={{ '--uf-spin-dur': '40s' }} aria-hidden="true">
        <PlateMark size={200} className="text-white" />
      </div>
      <div className="absolute -right-8 bottom-0 opacity-15 uf-spin" style={{ '--uf-spin-dur': '50s', animationDirection: 'reverse' }} aria-hidden="true">
        <PlateMark size={160} className="text-white" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20 flex flex-col md:flex-row items-center justify-between gap-8">
        <div className="text-center md:text-left max-w-xl">
          <h3
            className="font-extrabold text-4xl md:text-5xl leading-[0.95] uppercase tracking-tight"
            style={{ fontFamily: fontDisplay }}
          >
            Walk in this week.
            <span className="block text-zinc-900">Lift on Monday.</span>
          </h3>
          <p className="text-white/90 mt-3 text-base md:text-lg">
            7 free days. Free intro session. No card, no contract, no pressure tour. The only thing standing between you and a barbell is the door.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <a
            href="#start"
            className="uf-cta-sheen inline-flex items-center gap-2 px-7 py-4 rounded-md bg-zinc-900 text-white font-extrabold text-lg uppercase tracking-wider shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all whitespace-nowrap"
          >
            <Flame size={20} />Start my trial
          </a>
          <a
            href={`tel:${phoneRaw}`}
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md bg-black/30 hover:bg-black/40 backdrop-blur border border-white/30 text-white font-extrabold uppercase tracking-wider transition-colors whitespace-nowrap"
          >
            <Phone size={18} />{phone}
          </a>
        </div>
      </div>
    </section>
  );
}

function Contact({ contact, hours, fontDisplay }) {
  const phoneRaw = contact.phone.replace(/[^\d+]/g, '');
  const [form, setForm] = useState({ name: '', email: '', phone: '', goal: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  function onSubmit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setSubmitted(true);
  }

  return (
    <section id="contact" className="py-20 md:py-28 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-4 md:px-6 grid md:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16">
        <div>
          <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-orange-400 mb-2">Reach out</p>
          <h2
            className="font-extrabold text-5xl md:text-6xl text-white leading-[0.95] uppercase tracking-tight mb-6"
            style={{ fontFamily: fontDisplay }}
          >
            Tell us your
            <span className="block text-red-500">goal.</span>
          </h2>
          <p className="text-zinc-400 mb-8 leading-relaxed">
            Drop a line — your trial week + intro session is on us. We reply within the day, no robo-tour, no upsells.
          </p>

          {submitted ? (
            <div className="rounded-2xl bg-red-500/10 border border-red-500/30 p-7 text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-red-600 text-white flex items-center justify-center mb-4">
                <Check size={26} />
              </div>
              <p className="font-extrabold text-white text-xl mb-2 uppercase tracking-wide" style={{ fontFamily: fontDisplay }}>
                Got it, {form.name.split(' ')[0]}.
              </p>
              <p className="text-sm text-zinc-300 leading-relaxed">
                One of the coaches will text you within the day to set up your intro session and get you on the schedule.
              </p>
              <button
                type="button"
                onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', goal: '', message: '' }); }}
                className="mt-4 text-sm text-red-400 font-extrabold uppercase tracking-wider hover:text-white"
              >
                Send another
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-extrabold uppercase tracking-wider text-zinc-300">Name</span>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1 w-full px-3.5 py-2.5 rounded-md border border-zinc-800 bg-zinc-900 text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/30 outline-none text-sm"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-extrabold uppercase tracking-wider text-zinc-300">Phone</span>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-1 w-full px-3.5 py-2.5 rounded-md border border-zinc-800 bg-zinc-900 text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/30 outline-none text-sm"
                  />
                </label>
              </div>
              <label className="block">
                <span className="text-xs font-extrabold uppercase tracking-wider text-zinc-300">Email</span>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 w-full px-3.5 py-2.5 rounded-md border border-zinc-800 bg-zinc-900 text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/30 outline-none text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs font-extrabold uppercase tracking-wider text-zinc-300">What's the goal?</span>
                <select
                  value={form.goal}
                  onChange={(e) => setForm({ ...form, goal: e.target.value })}
                  className="mt-1 w-full px-3.5 py-2.5 rounded-md border border-zinc-800 bg-zinc-900 text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/30 outline-none text-sm"
                >
                  <option value="">Pick one — or skip</option>
                  <option value="strength">Get stronger</option>
                  <option value="conditioning">Get in shape</option>
                  <option value="weight">Lose weight</option>
                  <option value="community">Find a community</option>
                  <option value="other">Something else</option>
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-extrabold uppercase tracking-wider text-zinc-300">Anything else?</span>
                <textarea
                  rows={3}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Injuries, schedule constraints, total beginner — whatever it is, tell us."
                  className="mt-1 w-full px-3.5 py-2.5 rounded-md border border-zinc-800 bg-zinc-900 text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/30 outline-none text-sm placeholder:text-zinc-600"
                />
              </label>
              <button
                type="submit"
                className="uf-cta-sheen w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-md bg-red-600 hover:bg-red-700 text-white font-extrabold uppercase tracking-wider shadow-md hover:shadow-lg transition-all"
              >
                Claim my free week <ArrowRight size={16} />
              </button>
            </form>
          )}
        </div>

        <div>
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900 overflow-hidden shadow-sm">
            <div className="aspect-[16/10] bg-gradient-to-br from-red-900/30 via-zinc-900 to-orange-900/20 relative overflow-hidden">
              <div className="absolute inset-0 opacity-30" style={{
                backgroundImage:
                  'repeating-linear-gradient(0deg, rgba(239,68,68,0.15) 0 1px, transparent 1px 28px), repeating-linear-gradient(90deg, rgba(239,68,68,0.15) 0 1px, transparent 1px 28px)',
              }} aria-hidden="true" />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                <span className="w-14 h-14 rounded-2xl bg-red-600 text-white flex items-center justify-center mb-3 shadow-lg">
                  <MapPin size={22} />
                </span>
                <p className="font-bold text-white">{contact.address}</p>
                <p className="text-sm text-zinc-400 mt-1">East 6th — corner of Comal · two blocks from the metro</p>
              </div>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur border border-zinc-700 text-xs font-extrabold uppercase tracking-wider text-zinc-200">
                <span className="w-2 h-2 rounded-full bg-red-500 uf-flash" />
                Open now · members 24/7
              </div>
            </div>
            <div className="divide-y divide-zinc-800">
              <ContactRow icon={Phone} href={`tel:${phoneRaw}`} label="Call us" value={contact.phone} />
              <ContactRow icon={Mail} href={`mailto:${contact.email}`} label="Email" value={contact.email} />
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-zinc-800 bg-zinc-900 shadow-sm overflow-hidden">
            <div className="px-5 py-4 bg-red-500/10 border-b border-zinc-800 flex items-center gap-2">
              <Clock size={16} className="text-red-400" />
              <span className="font-extrabold text-white text-sm uppercase tracking-[0.18em]">Hours</span>
            </div>
            {hours.map((h) => (
              <div
                key={h.day}
                className="flex items-center justify-between px-5 py-3 border-t border-zinc-800 first:border-t-0"
              >
                <span className="font-bold text-white">{h.day}</span>
                <span className="text-zinc-400 font-medium">{h.time}</span>
              </div>
            ))}
          </div>

          <div className="mt-6 rounded-2xl border border-orange-500/30 bg-orange-500/5 p-5 flex items-start gap-3">
            <span className="w-10 h-10 rounded-lg bg-orange-500 text-zinc-900 flex items-center justify-center flex-shrink-0">
              <Megaphone size={18} />
            </span>
            <div>
              <p className="font-extrabold text-orange-300 leading-tight uppercase tracking-wider text-sm">Pro tip</p>
              <p className="text-sm text-zinc-300 mt-1 leading-relaxed">
                Book the 5:30 AM Monday slot for your first class — smaller class, more coach attention, and you start your week on a barbell.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon: Icon, href, label, value }) {
  const inner = (
    <div className="flex items-center gap-4 group px-5 py-4">
      <span className="w-11 h-11 flex-shrink-0 rounded-lg bg-red-500/15 text-red-400 flex items-center justify-center group-hover:bg-red-600 group-hover:text-white transition-colors">
        <Icon size={18} />
      </span>
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 font-extrabold">{label}</div>
        <div className="text-white font-bold">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} className="block">{inner}</a> : <div>{inner}</div>;
}

function Footer({ demo, fontDisplay }) {
  return (
    <footer className="bg-black text-zinc-300 relative overflow-hidden border-t border-zinc-900">
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-14 pb-12 grid md:grid-cols-3 gap-10">
        <div>
          <div className="flex items-center gap-2.5 mb-4">
            <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center text-zinc-900">
              <PlateMark size={20} className="text-zinc-900" />
            </span>
            <span
              className="font-extrabold text-2xl text-white uppercase tracking-wide"
              style={{ fontFamily: fontDisplay }}
            >
              {demo.name}
            </span>
          </div>
          <p className="text-sm leading-relaxed text-zinc-400 mb-4">
            Coach-led strength and conditioning, calibrated barbells, real coaching, real community. Open 5 AM to 7 PM coached, 24/7 for members.
          </p>
          <div className="flex items-center gap-1 text-xs text-zinc-400">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} size={12} className="fill-orange-400 text-orange-400" />
            ))}
            <span className="ml-1.5 font-extrabold uppercase tracking-wider">4.9 · 380+ Google reviews</span>
          </div>
        </div>

        <div>
          <h4 className="text-white font-extrabold text-sm uppercase tracking-[0.22em] mb-4">Programs</h4>
          <ul className="space-y-2 text-sm">
            {demo.services.map((s) => (
              <li key={s.title}>
                <a href="#classes" className="hover:text-white transition-colors">{s.title}</a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-white font-extrabold text-sm uppercase tracking-[0.22em] mb-4">Find us</h4>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2">
              <Phone size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
              <a href={`tel:${demo.contact.phone.replace(/[^\d+]/g, '')}`} className="hover:text-white">{demo.contact.phone}</a>
            </li>
            <li className="flex items-start gap-2">
              <Mail size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
              <a href={`mailto:${demo.contact.email}`} className="hover:text-white break-all">{demo.contact.email}</a>
            </li>
            <li className="flex items-start gap-2">
              <MapPin size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
              <span>{demo.contact.address}</span>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-zinc-500">
          <p>&copy; {new Date().getFullYear()} {demo.name}. All rights reserved.</p>
          <p>
            Demo site built by{' '}
            <Link to="/" className="text-orange-400 font-extrabold uppercase tracking-wider hover:text-white">Sudco Solutions</Link>
          </p>
        </div>
      </div>
    </footer>
  );
}

export default UrbanFitnessDemo;
